import { fetchEventSource } from '@microsoft/fetch-event-source';
import { noop } from '../utils';
import { EventSourceLogger } from './EventSourceLogger';
import {
    ConnectionAbruptedError,
    EventSourceError,
    FatalError,
    MessageError,
    NetworkError,
} from './errors';
import type { EventSourceMessage, FetchEventSourceInit } from '@microsoft/fetch-event-source';

export interface EventsSourceConfig {
    maxRetryAttempts?: number;
    retryIntervalMs?: number;
    enableLogger?: boolean;
}

export class EventSource {
    private isConnected: boolean;
    private abortController?: AbortController;
    private retryAttemptsAcc: number;
    private retryAttemptsMax?: number;
    private retryIntervalMs?: number;
    private logger?: EventSourceLogger;

    public onMessage: (ev: EventSourceMessage) => void;
    public onConnectionError: (error: EventSourceError) => void;

    constructor({ maxRetryAttempts, retryIntervalMs, enableLogger }: EventsSourceConfig = {}) {
        this.isConnected = false;
        this.retryAttemptsAcc = 0;
        this.retryAttemptsMax = maxRetryAttempts;
        this.retryIntervalMs = retryIntervalMs;
        this.onMessage = noop;
        this.onConnectionError = noop;

        if (enableLogger) {
            this.logger = new EventSourceLogger();
        }
    }

    hasConnection() {
        return this.isConnected;
    }

    async connect({ url, ...fetchParams }: EventSourceConnectParams) {
        const abortController = new AbortController();
        this.abortController = abortController;
        this.retryAttemptsAcc = 0;

        try {
            this.isConnected = true;
            this.logger?.connectionRequest(url);

            // This promise will stay in pending state, while the connection is established.
            // Promise would resolve on connection abort from client side
            // Promise would reject on error during connection after certain amount of retries
            await fetchEventSource(url, {
                ...fetchParams,
                signal: abortController.signal,
                onopen: (...args) => this._onOpen(...args),
                onmessage: (...args) => this._onMessage(...args),
                onclose: (...args) => this._onClose(...args),
                onerror: (...args) => this._onError(...args),
            });
        } catch (error) {
            this.logger?.connectionFatalError(error);
            this.onConnectionError(new EventSourceError({ cause: error, request: { url } }));

            abortController.abort();
        }

        this.isConnected = false;
        this.logger?.connectionClosed();
    }

    disconnect() {
        this.abortController?.abort();
    }

    private _onOpen(response: Response): Promise<void> {
        if (response.ok && response.headers.get('content-type') === 'text/event-stream') {
            this.retryAttemptsAcc = 0;

            this.logger?.connectionSuccess();

            return Promise.resolve(); // everything's good
        } else if (response.status >= 400 && response.status < 500 && response.status !== 429) {
            // client-side errors are usually non-retriable:
            const error = new NetworkError(response);
            throw new FatalError({ cause: error });
        } else {
            throw new NetworkError(response);
        }
    }

    private _onMessage(msg: EventSourceMessage): void {
        // if the server emits an error message, throw an exception
        // so it gets handled by the onerror callback below:
        if (msg.event === 'FatalError') {
            const error = new MessageError(`${msg.event} event received`);
            throw new FatalError({ cause: error });
        }

        this.logger?.messageReceived(msg);

        return this.onMessage(msg);
    }

    private _onClose(): void {
        this.logger?.connectionClosedFromServer();

        // if the server closes the connection unexpectedly, retry:
        throw new ConnectionAbruptedError();
    }

    private _onError(err: unknown): number | void | null | undefined {
        if (err instanceof FatalError) {
            throw err.cause; // rethrow to reject `fetchEventSource` promise
        } else {
            this.logger?.connectionError(err);

            this.retryAttemptsAcc++;

            if (this.retryAttemptsMax && this.retryAttemptsAcc >= this.retryAttemptsMax) {
                throw err;
            }

            this.logger?.connectionRetry();

            return this.retryIntervalMs;
        }
    }
}

export type EventSourceConnectParams = Omit<
    FetchEventSourceInit,
    'onopen' | 'onmessage' | 'onclose' | 'onerror' | 'signal'
> & { url: string };

export { EventSourceMessage };
